import React, { useEffect, useMemo, useState } from 'react'
import { CircularProgress } from 'react-cssfx-loading'
import i18n from 'i18next'
import { YearMonth } from '../api/listByStatusRequest'
import { usePublicReservationsByStatus } from '../hooks/usePublicReservationsByStatus'
import { CalendarToolbar } from './CalendarToolbar'
import { DayEventsModal } from './DayEventsModal'
import { DayView } from './DayView'
import {
  addDays,
  addMonths,
  ensureWorkingDay,
  formatToolbarLabel,
  shiftToWorkingDay,
} from './dateUtils'
import { MonthView } from './MonthView'
import { resolveAvailabilitySchedule } from './scheduleConfig'
import { SpaceSidebar } from './SpaceSidebar'
import {
  CalendarBody,
  CalendarGrid,
  CalendarLoadingContent,
  CalendarShell,
  CalendarStatusMessage,
  CalendarStatusOverlay,
  RetryAvailabilityButton,
} from './style'
import {
  AvailabilityCalendarLabels,
  AvailabilityCalendarRoom,
  AvailabilityCalendarView,
  AvailabilityScheduleInput,
} from './types'
import { WeekView } from './WeekView'

export interface AvailabilityCalendarProps {
  rooms: AvailabilityCalendarRoom[]
  settings?: AvailabilityScheduleInput | null
  isActive?: boolean
  variant?: 'modal' | 'page'
}

export const AvailabilityCalendar = ({
  rooms,
  settings = null,
  isActive = true,
  variant = 'modal',
}: AvailabilityCalendarProps): JSX.Element => {
  const schedule = useMemo(() => resolveAvailabilitySchedule(settings), [settings])
  const [view, setView] = useState<AvailabilityCalendarView>('week')
  const [date, setDate] = useState(() => ensureWorkingDay(new Date(), schedule.workingDays))
  const [visibleRoomIds, setVisibleRoomIds] = useState<string[]>(() => rooms.map(room => room.id))
  const [dayEventsDate, setDayEventsDate] = useState<Date | null>(null)
  const roomsKey = rooms.map(room => room.id).join('|')

  const labels: AvailabilityCalendarLabels = {
    title: i18n.t('meetingRoom.public.availability.title'),
    spaces: i18n.t('meetingRoom.public.availability.spaces'),
    showAll: i18n.t('meetingRoom.public.availability.showAll'),
    unavailable: i18n.t('meetingRoom.public.availability.unavailable'),
    today: i18n.t('meetingRoom.public.availability.today'),
    month: i18n.t('meetingRoom.public.availability.month'),
    week: i18n.t('meetingRoom.public.availability.week'),
    day: i18n.t('meetingRoom.public.availability.day'),
    previous: i18n.t('meetingRoom.public.availability.prev'),
    next: i18n.t('meetingRoom.public.availability.next'),
    close: i18n.t('meetingRoom.public.availability.close'),
    moreEvents: count =>
      i18n.t('meetingRoom.public.availability.moreEvents', { count }),
    reservationsCount: count =>
      i18n.t('meetingRoom.public.availability.reservationsCount', { count }),
  }

  const loadErrorLabel = i18n.t('meetingRoom.public.availability.loadError')
  const retryLabel = i18n.t('meetingRoom.public.availability.retry')
  const loadingLabel = i18n.t('meetingRoom.public.availability.loading')

  useEffect(() => {
    if (!isActive) return
    setVisibleRoomIds(rooms.map(room => room.id))
    setDate(ensureWorkingDay(new Date(), schedule.workingDays))
    setView('week')
    setDayEventsDate(null)
  }, [isActive, roomsKey, schedule.workingDays])

  const focusedMonth = useMemo(
    (): YearMonth => ({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
    }),
    [date]
  )

  const { events, isLoading, isError, refetch } = usePublicReservationsByStatus({
    enabled: isActive && rooms.length > 0,
    focusedMonth,
    rooms,
    unavailableTitle: labels.unavailable,
  })

  const filteredEvents = useMemo(
    () => events.filter(event => visibleRoomIds.includes(event.roomId)),
    [events, visibleRoomIds]
  )

  const handleToggleAll = (): void => {
    const allSelected = rooms.length > 0 && visibleRoomIds.length === rooms.length
    setVisibleRoomIds(allSelected ? [] : rooms.map(room => room.id))
  }

  const handleToggleRoom = (roomId: string): void => {
    setVisibleRoomIds(prev =>
      prev.includes(roomId)
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    )
  }

  const handleNavigate = (direction: -1 | 1): void => {
    setDate(prev => {
      if (view === 'month') return addMonths(prev, direction)
      if (view === 'week') return addDays(prev, direction * 7)
      return shiftToWorkingDay(prev, direction, schedule.workingDays)
    })
  }

  const handleToday = (): void => {
    setDate(ensureWorkingDay(new Date(), schedule.workingDays))
  }

  const handleSelectDay = (day: Date): void => {
    setDate(ensureWorkingDay(day, schedule.workingDays))
    setView('day')
  }

  const handleShowMore = (day: Date): void => {
    setDayEventsDate(day)
  }

  const showInitialLoading = isLoading && events.length === 0
  const showError = isError && events.length === 0

  return (
    <CalendarShell $variant={variant}>
      <CalendarBody>
        <SpaceSidebar
          rooms={rooms}
          visibleRoomIds={visibleRoomIds}
          spacesLabel={labels.spaces}
          showAllLabel={labels.showAll}
          onToggleAll={handleToggleAll}
          onToggleRoom={handleToggleRoom}
        />

        <CalendarGrid>
          <CalendarToolbar
            labels={labels}
            view={view}
            dateLabel={formatToolbarLabel(view, date)}
            onNavigate={handleNavigate}
            onToday={handleToday}
            onViewChange={setView}
          />

          {view === 'week' && (
            <WeekView
              date={date}
              events={filteredEvents}
              schedule={schedule}
              onSelectDay={handleSelectDay}
            />
          )}
          {view === 'month' && (
            <MonthView
              date={date}
              events={filteredEvents}
              schedule={schedule}
              labels={labels}
              onSelectDay={handleSelectDay}
              onShowMore={handleShowMore}
            />
          )}
          {view === 'day' && (
            <DayView
              date={date}
              events={filteredEvents}
              schedule={schedule}
            />
          )}

          {(showInitialLoading || showError) && (
            <CalendarStatusOverlay>
              {showError ? (
                <>
                  <CalendarStatusMessage>{loadErrorLabel}</CalendarStatusMessage>
                  <RetryAvailabilityButton type="button" onClick={() => refetch()}>
                    {retryLabel}
                  </RetryAvailabilityButton>
                </>
              ) : (
                <CalendarLoadingContent>
                  <CircularProgress color="var(--primary)" width="36px" height="36px" />
                  <CalendarStatusMessage>{loadingLabel}</CalendarStatusMessage>
                </CalendarLoadingContent>
              )}
            </CalendarStatusOverlay>
          )}
        </CalendarGrid>
      </CalendarBody>

      <DayEventsModal
        date={dayEventsDate}
        events={filteredEvents}
        labels={labels}
        onClose={() => setDayEventsDate(null)}
      />
    </CalendarShell>
  )
}
